import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, HandCoins, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { api, money, formatApiError } from "@/lib/api";
import { FileUpload } from "@/components/FileUpload";

const selectCls = "w-full rounded-xl border border-[#e4d8c4] bg-white px-3 py-2.5 text-sm text-[#00334a]";
const STAGES = ["prospecting", "contacted", "pledged", "received", "recognized"];
const STAGE_COLOR = {
  prospecting: "bg-[#efe9dd] text-[#5a5849]",
  contacted: "bg-[#efe6d6] text-[#3f6670]",
  pledged: "bg-[#f6ecd7] text-[#9c6a2e]",
  received: "bg-[#e8e7d6] text-[#5f6a48]",
  recognized: "bg-[#f3e7db] text-[#b05e3c]",
};
const CATEGORIES = [
  { v: "swimmer", l: "Swimmer Sponsorship" },
  { v: "event", l: "Event Sponsorship" },
  { v: "in-kind", l: "In-Kind Donation" },
  { v: "auction", l: "Auction Item" },
];

const empty = {
  business_name: "", contact_name: "", contact_email: "", contact_phone: "",
  category: "swimmer", tier: "", ask_amount: 0, pledged_amount: 0, received_amount: 0,
  stage: "prospecting", in_kind_description: "", notes: "", logo_url: "",
};

export default function SponsorsTab() {
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get("/sponsors").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  useEffect(() => {
    load();
    api.get("/sponsors/tiers").then(({ data }) => setTiers(data)).catch(() => {});
  }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s.id); setForm({ ...empty, ...s }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        ask_amount: Number(form.ask_amount) || 0,
        pledged_amount: Number(form.pledged_amount) || 0,
        received_amount: Number(form.received_amount) || 0,
      };
      if (editing) await api.put(`/sponsors/${editing}`, payload);
      else await api.post("/sponsors", payload);
      toast.success(editing ? "Sponsor updated" : "Sponsor added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sponsor record?")) return;
    await api.delete(`/sponsors/${id}`);
    toast.success("Deleted");
    load();
  };

  const totals = items.reduce((a, s) => ({ pledged: a.pledged + (s.pledged_amount || 0), received: a.received + (s.received_amount || 0) }), { pledged: 0, received: 0 });
  const catTiers = tiers.filter((t) => t.category === form.category);

  return (
    <div className="space-y-6" data-testid="admin-sponsors">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="hf-eyebrow">Business & in-kind</span>
          <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#00334a]">Sponsorship Pipeline</h1>
        </div>
        <Button onClick={openNew} className="rounded-full hf-btn-coral font-bold" data-testid="sponsor-add-tier-button">
          <Plus className="mr-2 h-4 w-4" /> Add sponsor
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-[#e4d8c4] bg-white p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]"><HandCoins className="h-4 w-4 text-[#c99a4e]" /> Total pledged</div>
          <div className="mt-2 font-heading text-3xl font-extrabold text-[#c99a4e]">{money(totals.pledged)}</div>
        </div>
        <div className="rounded-2xl border border-[#e4d8c4] bg-white p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]"><CheckCircle2 className="h-4 w-4 text-[#87856c]" /> Total received</div>
          <div className="mt-2 font-heading text-3xl font-extrabold text-[#87856c]">{money(totals.received)}</div>
        </div>
      </div>

      {/* Tier reference */}
      <div className="rounded-2xl border border-[#e4d8c4] bg-[#f8f3e9] p-5">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]">Sponsorship tiers reference</div>
        <div className="grid gap-3 md:grid-cols-3">
          {tiers.map((t) => (
            <div key={t.name} className="rounded-xl border border-[#e4d8c4] bg-white p-3">
              <div className="flex items-center justify-between">
                <span className="font-heading font-bold text-[#00334a]">{t.name}</span>
                <span className="text-sm font-bold text-[#c97049]">{t.amount}</span>
              </div>
              <p className="mt-1 text-xs text-[#7c7a6a]">{t.blurb}</p>
            </div>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#e4d8c4] bg-white p-12 text-center">
          <Building2 className="mx-auto h-10 w-10 text-[#e4d8c4]" />
          <p className="mt-3 font-semibold text-[#00334a]">No sponsors tracked yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#e4d8c4] bg-white p-4" data-testid={`sponsor-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {s.logo_url && <img src={s.logo_url} alt="" className="h-8 w-8 rounded object-contain bg-white border border-[#e4d8c4]" />}
                  <span className="font-heading font-bold text-[#00334a]">{s.business_name}</span>
                  {s.tier && <span className="rounded-full bg-[#efe6d6] px-2 py-0.5 text-[10px] font-bold text-[#3f6670]">{s.tier}</span>}
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STAGE_COLOR[s.stage]}`}>{s.stage}</span>
                </div>
                <div className="text-xs text-[#7c7a6a]">
                  {s.contact_name}{s.contact_email ? ` · ${s.contact_email}` : ""}
                  {s.category === "in-kind" && s.in_kind_description ? ` · In-kind: ${s.in_kind_description}` : ""}
                </div>
              </div>
              <div className="text-right text-sm">
                <div className="font-semibold text-[#65949c]">{money(s.pledged_amount)} pledged</div>
                <div className="text-xs text-[#87856c]">{money(s.received_amount)} received</div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(s)} className="h-8 w-8 text-[#3f6670]" data-testid={`sponsor-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(s.id)} className="h-8 w-8 text-[#c97049]" data-testid={`sponsor-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="sponsor-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit sponsor" : "Add sponsor"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Business name</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} required data-testid="sponsor-business-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Contact name</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Contact email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, tier: "" })} className={selectCls} data-testid="sponsor-category-select">
                  {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Tier</Label>
                <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className={selectCls} data-testid="sponsor-tier-select">
                  <option value="">— none —</option>
                  {catTiers.map((t) => <option key={t.name} value={t.name}>{t.name} ({t.amount})</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Ask ($)</Label><Input type="number" min="0" value={form.ask_amount} onChange={(e) => setForm({ ...form, ask_amount: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Pledged ($)</Label><Input type="number" min="0" value={form.pledged_amount} onChange={(e) => setForm({ ...form, pledged_amount: e.target.value })} data-testid="sponsor-pledged-input" /></div>
              <div className="space-y-1.5"><Label>Received ($)</Label><Input type="number" min="0" value={form.received_amount} onChange={(e) => setForm({ ...form, received_amount: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Stage</Label>
              <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className={`${selectCls} capitalize`} data-testid="sponsor-stage-select">
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {form.category === "in-kind" && (
              <div className="space-y-1.5"><Label>In-kind description</Label><Input value={form.in_kind_description} onChange={(e) => setForm({ ...form, in_kind_description: e.target.value })} placeholder="e.g. 20 gift cards, raffle prizes" /></div>
            )}
            <div className="space-y-1.5">
              <Label>Logo</Label>
              <div className="flex items-center gap-3">
                <FileUpload scope="logo" label={form.logo_url ? "Replace logo" : "Upload logo"} testId="sponsor-logo-upload"
                  onUploaded={(d) => setForm({ ...form, logo_url: d.url })} />
                {form.logo_url && <img src={form.logo_url} alt="logo" className="h-10 w-10 rounded object-contain bg-white border border-[#e4d8c4]" />}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
            <DialogFooter><Button type="submit" disabled={busy} className="rounded-full hf-btn-coral font-bold" data-testid="sponsor-save-button">{busy ? "Saving…" : "Save sponsor"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
